import { useState } from "react";
import { Rocket, X } from "lucide-react";

/**
 * Top notification bar shown at the very top of the Dashboard. Invites the
 * vendor to send their first comprobante to the official WhatsApp number to
 * activate the auto-validator.
 *
 * Visibility:
 *   - Reads `REACT_APP_WHATSAPP_NUMBER` from the env. If missing, the banner
 *     simply doesn't render (no garbage URL).
 *   - Dismissed via the "X" button → persists the choice in localStorage
 *     under `sinpeflow_hide_onboarding_banner` so the user isn't nagged.
 *   - Initial visibility is computed lazily during state init to avoid a
 *     flash of the banner before the effect runs.
 *
 * Accessibility:
 *   - role="alert" + aria-live="polite" so screen readers announce it once.
 *   - The "X" button has an accessible label.
 */
const STORAGE_KEY = "sinpeflow_hide_onboarding_banner";

// Strip everything that is not a digit (+506-… → 506…) for the wa.me link.
const stripToDigits = (raw) => (raw || "").replace(/\D/g, "");

// Pretty-print +1 (934) 203-4693 from "+19342034693" — best-effort,
// degrades gracefully for non-NANP numbers.
const formatForDisplay = (raw) => {
    const digits = stripToDigits(raw);
    if (!digits) return "";
    if (digits.length === 11 && digits.startsWith("1")) {
        return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
    }
    return raw.startsWith("+") ? raw : `+${digits}`;
};

const isDismissed = () => {
    try {
        return window.localStorage.getItem(STORAGE_KEY) === "1";
    } catch (err) {
        // localStorage blocked (private mode, browser policy) → show banner
        console.warn("[onboarding-banner] localStorage read failed:", err);
        return false;
    }
};

export default function OnboardingBanner() {
    const rawNumber = process.env.REACT_APP_WHATSAPP_NUMBER || "";
    const digits = stripToDigits(rawNumber);
    const displayNumber = formatForDisplay(rawNumber);
    const waLink = digits ? `https://wa.me/${digits}` : "";

    // Lazy initializer — runs once on mount. Avoids a flash of the banner
    // for users who have already dismissed it.
    const [visible, setVisible] = useState(() => Boolean(waLink) && !isDismissed());

    const dismiss = () => {
        setVisible(false);
        try {
            window.localStorage.setItem(STORAGE_KEY, "1");
        } catch (err) {
            // localStorage can throw in private mode or when disabled by
            // user policy — banner just won't persist across reloads.
            console.warn("[onboarding-banner] localStorage write failed:", err);
        }
    };

    if (!visible) return null;

    return (
        <div
            role="alert"
            aria-live="polite"
            data-testid="onboarding-banner"
            className="w-full bg-gradient-to-r from-[#EFF6FF] via-[#DBEAFE] to-[#EFF6FF] border-b border-[#BFDBFE]"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex items-center gap-4">
                <div className="hidden sm:flex h-9 w-9 rounded-full bg-white border border-[#BFDBFE] items-center justify-center flex-shrink-0 shadow-sm">
                    <Rocket className="w-4 h-4 text-[#1E3A8A]" />
                </div>

                <p className="flex-1 text-sm leading-snug text-[#0F172A]">
                    <span className="mr-1.5" aria-hidden="true">🚀</span>
                    <span className="font-medium">Paso final:</span>{" "}
                    <span>
                        Envía una captura de tu comprobante SINPE por WhatsApp al{" "}
                    </span>
                    <a
                        href={waLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-[#1E3A8A] underline decoration-2 decoration-[#1E3A8A]/40 underline-offset-2 hover:decoration-[#1E3A8A] transition-colors whitespace-nowrap"
                        data-testid="onboarding-banner-wa-link"
                    >
                        {displayNumber}
                    </a>{" "}
                    <span>para activar tu validador automático.</span>
                </p>

                <button
                    type="button"
                    onClick={dismiss}
                    aria-label="Cerrar mensaje de bienvenida"
                    className="flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-[#1E40AF] hover:bg-white/70 transition-colors"
                    data-testid="onboarding-banner-close"
                >
                    <X className="w-4 h-4" strokeWidth={2.25} />
                </button>
            </div>
        </div>
    );
}
